import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, first, from } from 'rxjs';

import { List, PathService } from '@site-mate/dashpivot-shared-library';

import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { HttpClientService } from 'app/shared/service/http-client.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { UserService } from 'app/user/user.service';

@Injectable({ providedIn: 'root' })
export class ListsService {
  constructor(
    private readonly httpClientService: HttpClientService,
    private readonly pathService: PathService,
    private readonly errorHandler: ErrorHandler,
    private readonly routeParamsService: RouteParamsService,
    private readonly userService: UserService,
    private readonly router: Router,
  ) {}

  getList(listId: string) {
    return this.httpClientService.get<List>(`v1/lists/${listId}`);
  }

  getCompanyMetaDataList(companyId: string) {
    return this.httpClientService.get<List[]>(`v1/companies/${companyId}/lists`);
  }

  getProjectMetaDataList(projectId: string) {
    return this.httpClientService.get<List[]>(`v1/projects/${projectId}/lists`);
  }

  // We want to move towards the unified vision of global items and folders, and away from the notion of "projects" and "teams"
  // So, whenever possible, we want to expose a single point where we still talk with the old model, so it's easier to migrate
  saveFolderList(path: string, listName: string) {
    const folderIds = this.pathService.extractFolderIds(path);
    const newList = { name: listName, items: [] };

    if (!folderIds[0]) {
      this.debugContext(path, folderIds);
    }

    if (folderIds.length === 1) {
      return this.saveCompanyList(folderIds[0], newList);
    }

    if (!folderIds[folderIds.length - 1]) {
      this.debugContext(path, folderIds);
    }

    return this.saveProjectList(folderIds[folderIds.length - 1], newList);
  }

  private saveProjectList(projectId, list) {
    return this.httpClientService.post(`v1/projects/${projectId}/lists`, list);
  }

  private saveCompanyList(companyId, list) {
    return this.httpClientService.post(`v1/companies/${companyId}/lists`, list);
  }

  private debugContext(path: string, folderIds: string[]) {
    combineLatest([
      this.routeParamsService.get('company'),
      this.routeParamsService.get('project'),
      this.routeParamsService.get('team'),
      this.userService.currentUser,
    ])
      .pipe(first())
      .subscribe(([company, project, team, user]) => {
        this.errorHandler.handleForDebug(new Error('Missing projectId for creating new list'), {
          routeParamsCompany: company,
          routeParamsProject: project,
          routeParamsTeam: team,
          userServiceCurrentUser: user,
          currentUrl: this.router.url,
          path,
          folderIds,
        });
      });
  }

  updateProjectList(projectId, listId, list) {
    return this.httpClientService.put(`v1/projects/${projectId}/lists/${listId}`, list);
  }

  updateCompanyList(companyId, listId, list) {
    return this.httpClientService.put(`v1/companies/${companyId}/lists/${listId}`, list);
  }

  updateGlobalItemProjectList(projectId, listId, updateValues) {
    return this.httpClientService.put(`projects/${projectId}/lists/${listId}`, updateValues);
  }

  updateGlobalItemCompanyList(companyId, listId, updateValues) {
    return this.httpClientService.put(`companies/${companyId}/lists/${listId}`, updateValues);
  }

  updateList(folder, listId, updateValues) {
    const folderMapping = {
      [AppHierarchy.Organisation]: () =>
        this.updateGlobalItemCompanyList(folder.company.id, listId, updateValues),
      [AppHierarchy.Project]: () => this.updateGlobalItemProjectList(folder.project.id, listId, updateValues),
    };

    return folderMapping[folder.hierarchy]();
  }

  removeProjectList(projectId: string, listId: string) {
    return this.httpClientService.remove<void>(`projects/${projectId}/lists/${listId}`);
  }

  removeProjectLists(projectId: string, listIds: string[]) {
    const removals = listIds.map((listId) => this.removeProjectList(projectId, listId).toPromise());
    return from(Promise.all(removals));
  }

  removeCompanyList(companyId: string, listId: string) {
    return this.httpClientService.remove<void>(`companies/${companyId}/lists/${listId}`);
  }

  removeCompanyLists(companyId: string, listIds: string[]) {
    const removals = listIds.map((listId) => this.removeCompanyList(companyId, listId).toPromise());
    return from(Promise.all(removals));
  }

  uploadUpdateProjectList(projectId, listId, formData) {
    return this.httpClientService.putUpload(
      `v1/projects/${projectId}/lists/${listId}/import-csv-file`,
      formData,
    );
  }

  uploadUpdateCompanyList(companyId: string, listId: string, formData) {
    return this.httpClientService.putUpload(
      `v1/companies/${companyId}/lists/${listId}/import-csv-file`,
      formData,
    );
  }

  getListForLibraryTemplate(templateId: string) {
    return this.httpClientService.get(`template-libraries/${templateId}/meta-lists`);
  }
}
